import { motion, useScroll, useTransform } from 'framer-motion';
import { useState, useEffect } from 'react';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const { scrollY } = useScroll();
  const navOpacity = useTransform(scrollY, [0, 100], [0.8, 0.95]);

  useEffect(() => {
    const unsubscribe = scrollY.on('change', (latest) => {
      setIsScrolled(latest > 50);
    });
    return () => unsubscribe();
  }, [scrollY]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <motion.nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-md"
      style={{
        backgroundColor: '#111c41',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
      }}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <motion.button
          onClick={scrollToTop}
          className="flex items-center cursor-pointer"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
        >
          <img
            src="https://static.readdy.ai/image/04bffda83af79335128d062f8508dd8e/3d43a0432d5745607a6ad4686bfd4a81.png"
            alt="INOCAP"
            className="h-12 w-auto"
          />
        </motion.button>

        {/* Navigation Links */}
        <div className="hidden md:flex items-center space-x-10">
          {['About', 'Focus', 'Value', 'Contact'].map((item, index) => (
            <motion.button
              key={item}
              onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))}
              className="text-white font-medium text-sm tracking-wide hover:text-blue-300 transition-colors duration-300 cursor-pointer whitespace-nowrap"
              whileHover={{ y: -2 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              {item}
            </motion.button>
          ))}
        </div>

        {/* CTA Button */}
        <motion.button
          onClick={() => scrollToSection('contact')}
          className="hidden md:block px-6 py-3 border border-white/30 text-white text-sm font-medium rounded-full hover:bg-blue-500/20 hover:border-blue-300/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          Get In Touch
        </motion.button>

        {/* Mobile Menu Button */}
        <motion.button
          className="md:hidden text-white p-2"
          whileTap={{ scale: 0.9 }}
        >
          <i className="ri-menu-line text-xl"></i>
        </motion.button>
      </div>

      {/* Progress Bar */}
      <motion.div
        className="h-1 bg-gradient-to-r from-blue-400 to-blue-600"
        style={{ 
          scaleX: useTransform(scrollY, [0, 4000], [0, 1]),
          transformOrigin: '0%'
        }}
      />
    </motion.nav>
  );
}
